// src/screens/Views.js
import React, { useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { getUrl, uploadData } from 'aws-amplify/storage';
import outputs from '../amplify_outputs.json';
import {
  Building2,
  CloudRain,
  FileText,
  Hammer,
  Image as ImageIcon,
  LayoutDashboard,
  Wallet,
  Receipt,
  Play,
  Clock,
  Plus,
  RefreshCw,
  Link as LinkIcon,
  Users,
  MessageSquare,
  Send,
  Paperclip,
  X,
  Eye,
  Check,
  Settings,
  Upload
} from 'lucide-react';
import { Card, Badge } from '../components/SharedUI';
import { MOCK_CLIENTS, MOCK_XERO_PROJECTS, MOCK_XERO_CONTACTS } from '../data/mockData';

const hasAPI = !!outputs?.API?.GraphQL?.endpoint;
const client = hasAPI ? generateClient({ authMode: 'userPool' }) : null;
const defaultXeroEndpoint = process.env.REACT_APP_XERO_SYNC_URL || 'https://hiw7vzi4mkn42u4ycs6uzr7ifi0hizob.lambda-url.ap-southeast-2.on.aws/';

const getXeroEndpoint = () => {
  if (typeof window === 'undefined') return defaultXeroEndpoint;
  return localStorage.getItem('xeroEndpointUrl') || defaultXeroEndpoint;
};

const callXeroSyncEndpoint = async (action) => {
  const xeroApiUrl = getXeroEndpoint();
  if (!xeroApiUrl) {
    throw new Error('Xero sync endpoint is not configured.');
  }

  const response = await fetch(xeroApiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ action }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.message || response.statusText || 'Xero sync failed');
  }

  return data;
};

export const ClientDashboard = ({ project }) => {
  if (!project) return <p>Loading project data...</p>;

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <header>
        <h2 className="text-2xl font-bold text-slate-800">Project Overview</h2>
        <p className="text-slate-500 mt-1">Welcome back. Here's a snapshot of your project's current status.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 flex items-center gap-4 hover:border-red-200 transition-colors">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
            <Building2 size={24} />
          </div>
          <div>
            <p className="text-sm text-slate-500 font-medium">Est. Completion</p>
            <p className="text-xl font-bold text-slate-800">Nov 15, 2026</p>
          </div>
        </Card>

        <Card className="p-6 flex items-center gap-4 hover:border-red-200 transition-colors">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-600">
            <LayoutDashboard size={24} />
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-end mb-1">
              <p className="text-sm text-slate-500 font-medium">Overall Progress</p>
              <span className="text-sm font-bold text-red-600">{project.progress}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="bg-red-500 h-2 rounded-full" style={{ width: `${project.progress}%` }} />
            </div>
          </div>
        </Card>

        <Card className="p-6 flex items-center gap-4 hover:border-red-200 transition-colors">
          <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
            <Wallet size={24} />
          </div>
          <div>
            <p className="text-sm text-slate-500 font-medium">Next Payment Due</p>
            <p className="text-xl font-bold text-slate-800">$45,000 <span className="text-sm font-normal text-slate-500 ml-1">in 12 days</span></p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <Card className="lg:col-span-2 overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-bold text-slate-800 text-lg">Recent Progress Photos</h3>
          </div>
          <div className="p-6 grid grid-cols-2 gap-4">
            <img src="https://placehold.co/600x400/e2e8f0/64748b?text=Framing" alt="Progress" className="w-full h-48 object-cover rounded-xl" />
            <img src="https://placehold.co/600x400/e2e8f0/64748b?text=Roofing" alt="Progress" className="w-full h-48 object-cover rounded-xl" />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-bold text-slate-800 mb-4">Site Weather</h3>
          <div className="flex items-center gap-4">
            <CloudRain size={40} className="text-slate-400" />
            <div>
              <p className="text-2xl font-bold text-slate-800">22°C</p>
              <p className="text-sm text-slate-500">Partly Cloudy</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export const ClientConstruction = ({ project }) => {
  if (!project) return <p>Loading project data...</p>;

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Construction Stage</h2>
          <p className="text-slate-500 mt-1">Follow the physical progress of your build.</p>
        </div>
        <div className="bg-red-50 text-red-800 px-4 py-2 rounded-lg font-semibold text-sm border border-red-200 flex items-center gap-2 shadow-sm w-fit">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          Current: {project.stage}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <Card className="p-6 sticky top-4">
            <h3 className="font-bold text-slate-800 mb-6 text-lg">Project Timeline</h3>
            <div className="space-y-6">
              {project.timeline?.map((step, idx) => (
                <div key={step.id} className={`rounded-3xl border border-slate-200 p-4 ${step.status === 'pending' ? 'opacity-60' : ''}`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step.status === 'completed' ? 'bg-red-600 text-white' : step.status === 'in-progress' ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
                      {step.status === 'completed' ? <Check size={14} strokeWidth={3} /> : step.status === 'in-progress' ? <Hammer size={14} /> : <span className="text-xs font-bold">{idx + 1}</span>}
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{step.status.replace('-', ' ')}</span>
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-1">{step.title}</h4>
                  <p className="text-xs text-slate-500 mb-2">{step.date}</p>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {project.updates?.length > 0 ? (
            project.updates.map((update) => (
              <Card key={update.id} className="overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex items-center gap-4 bg-slate-50/50">
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600 border border-slate-300">
                    {update.initials}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{update.author} posted an update</p>
                    <p className="text-xs text-slate-500">{update.date}</p>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="font-bold text-lg text-slate-800 mb-2">{update.title}</h4>
                  <p className="text-slate-600 mb-4 text-sm leading-relaxed">{update.content}</p>
                  {update.photos && update.photos.length > 0 && (
                    <div className={`grid gap-3 ${update.photos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {update.photos.map((photo, index) => (
                        <img key={index} src={photo} alt="Construction Progress" className="w-full h-48 md:h-64 object-cover rounded-xl border border-slate-200 hover:opacity-90 transition-opacity cursor-pointer" />
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-12 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mb-4">
                <Hammer size={24} />
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-1">No Updates Yet</h4>
              <p className="text-slate-500 max-w-sm">When your builder posts construction logs and progress photos, they will appear here.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export const ClientFinancials = ({ project }) => {
  const baseCostGst = project.baseActualCosts * 0.1;
  const baseCostInclGst = project.baseActualCosts + baseCostGst;
  const marginAmount = baseCostInclGst * (project.builderMarginPercent / 100);
  const marginGst = marginAmount * 0.1;
  const totalBillable = baseCostInclGst + marginAmount + marginGst;

  const formatC = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <header className="flex justify-between items-end">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-slate-800">Financial Overview</h2>
            <Badge variant="info" className="flex items-center gap-1">Synced with Xero</Badge>
          </div>
          <p className="text-slate-500 mt-1">Track your project budget and live costs securely fetched from Xero.</p>
        </div>
      </header>

      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          <div className="md:pr-6">
            <p className="text-sm text-slate-500 font-medium mb-1">Contract Total</p>
            <p className="text-2xl font-bold text-slate-800">{formatC(project.contractTotal)}</p>
          </div>
          <div className="md:px-6">
            <p className="text-sm text-slate-500 font-medium mb-1">Total Project Cost</p>
            <p className="text-2xl font-bold text-red-600">{formatC(project.contractTotal + 12500)}</p>
          </div>
          <div className="md:pl-6">
            <p className="text-sm text-slate-500 font-medium mb-1">Amount Paid</p>
            <p className="text-2xl font-bold text-slate-800">{formatC(project.amountPaid)}</p>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-slate-800 text-lg">Live Project Cost Breakdown</h3>
            <p className="text-xs text-slate-500">Real-time expenditure (Read-Only Integration)</p>
          </div>
        </div>
        <div className="p-6 flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600">Base Actual Costs (Excl. GST)</span>
            <span className="text-sm font-semibold text-slate-700">{formatC(project.baseActualCosts)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-slate-600">GST on Base Costs (10%)</span>
            <span className="text-sm font-semibold text-slate-700">{formatC(baseCostGst)}</span>
          </div>
          <div className="border-t border-slate-200 pt-3 mt-1 flex justify-between items-center">
            <span className="text-sm font-bold text-slate-700">Base Actual Costs (Incl. GST)</span>
            <span className="text-sm font-bold text-slate-700">{formatC(baseCostInclGst)}</span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-600">Builder Margin</span>
              <Badge variant="brand">{project.builderMarginPercent}%</Badge>
            </div>
            <span className="text-sm font-semibold text-slate-700">{formatC(marginAmount)}</span>
          </div>
          <div className="flex justify-between items-center mt-1">
            <span className="text-sm font-medium text-slate-600">GST on Builder Margin (10%)</span>
            <span className="text-sm font-semibold text-slate-700">{formatC(marginGst)}</span>
          </div>
          <div className="border-t border-slate-200 pt-4 mt-2 flex justify-between items-center">
            <span className="text-base font-bold text-slate-800">Total Billable to Date (Incl. GST)</span>
            <span className="text-xl font-bold text-blue-500">{formatC(totalBillable)}</span>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h3 className="font-bold text-slate-800 text-lg">Tasks & Expenses Ledger</h3>
            <p className="text-xs text-slate-500 mt-0.5">Itemized time and materials synced from Xero Projects</p>
          </div>
          <div className="flex gap-2">
            <Badge variant="info" className="flex items-center gap-1"><Clock size={12} /> Tasks</Badge>
            <Badge variant="brand" className="flex items-center gap-1"><Receipt size={12} /> Expenses</Badge>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white border-b border-slate-200">
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Date</th>
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Description</th>
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Type</th>
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Category</th>
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {project.tasksAndExpenses?.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                  <td className="p-4 text-sm text-slate-500 whitespace-nowrap">{item.date}</td>
                  <td className="p-4 text-sm text-slate-800 font-medium">{item.description}</td>
                  <td className="p-4">
                    <Badge variant={item.type === 'Expense' ? 'brand' : 'info'} className="uppercase tracking-wider text-[10px]">{item.type}</Badge>
                  </td>
                  <td className="p-4 text-sm text-slate-500">{item.category}</td>
                  <td className="p-4 text-sm text-slate-800 font-bold text-right">{formatC(item.amount)}</td>
                </tr>
              ))}
              {(!project.tasksAndExpenses || project.tasksAndExpenses.length === 0) && (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500 text-sm">No tasks or expenses recorded yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {project.tasksAndExpenses?.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-center">
            <button className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-2 transition-colors">
              View All 142 Transactions
            </button>
          </div>
        )}
      </Card>

      <Card className="overflow-hidden border-l-4 border-l-amber-400">
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
          <h3 className="font-bold text-slate-800 text-lg">Upcoming Payment Schedule</h3>
        </div>
        <div className="p-6 flex flex-col md:flex-row justify-between items-start md:items-center bg-amber-50/30 gap-4">
          <div>
            <p className="font-bold text-slate-800 text-lg">Enclosed Stage (Lock-up)</p>
            <p className="text-sm text-slate-500 flex items-center gap-2">
              <span>Invoice: INV-004</span>
              <span className="text-amber-600 flex items-center gap-1 font-medium"><Clock size={14} /> Due in 12 days</span>
            </p>
          </div>
          <div className="text-left md:text-right w-full md:w-auto">
            <p className="text-2xl font-bold text-slate-800 mb-2">$112,500</p>
            <button onClick={() => alert('Redirecting to secure payment gateway...')} className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-6 py-2.5 rounded-lg transition-colors shadow-sm">
              Pay Now
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export const ClientMedia = ({ project, onPreview, showToast }) => {
  const [filter, setFilter] = useState('all');
  const [mediaItems, setMediaItems] = useState(project?.media || []);
  const [secureUrls, setSecureUrls] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadType, setUploadType] = useState('photo');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);

  useEffect(() => {
    setMediaItems(project?.media || []);
  }, [project?.media]);

  useEffect(() => {
    if (!client || !project?.id || !client.models?.Media?.list) return;

    async function loadMedia() {
      try {
        const { data } = await client.models.Media.list({ filter: { projectId: { eq: project.id } } });
        setMediaItems(data);
      } catch (err) {
        console.warn('Unable to load project media from AWS:', err);
      }
    }

    loadMedia();
  }, [project?.id]);

  useEffect(() => {
    if (!client || mediaItems.length === 0) return;

    async function loadUrls() {
      const urls = {};
      await Promise.all(
        mediaItems.map(async (item) => {
          if (!item.url) return;
          try {
            const url = await getUrl(item.url, { level: 'protected' });
            urls[item.id] = url;
          } catch (err) {
            console.warn('Could not generate secure URL for media item:', item.url, err);
          }
        })
      );
      setSecureUrls(urls);
    }

    loadUrls();
  }, [mediaItems]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !uploadTitle) {
      showToast?.('Please select a file and enter a title before uploading.', 'error');
      return;
    }

    if (!project?.id) {
      showToast?.('Project information is missing. Cannot upload file.', 'error');
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    const key = `media/${project.id}/${Date.now()}-${selectedFile.name}`;
    try {
      const uploadResult = await uploadData(key, selectedFile, {
        level: 'protected',
        contentType: selectedFile.type || 'application/octet-stream'
      });
      const filePath = typeof uploadResult === 'string' ? uploadResult : uploadResult?.key || key;

      if (client?.models?.Media?.create) {
        await client.models.Media.create({
          projectId: project.id,
          fileName: uploadTitle,
          fileType: uploadType,
          url: filePath,
          uploadedBy: 'client',
          uploadDate: new Date().toISOString(),
          description: uploadTitle,
          visibility: 'protected'
        });
      }

      setSelectedFile(null);
      setUploadTitle('');
      setUploadType('photo');
      setUploadStatus('success');
      showToast?.('File uploaded securely to the portal.', 'success');

      if (client?.models?.Media?.list) {
        const { data } = await client.models.Media.list({ filter: { projectId: { eq: project.id } } });
        setMediaItems(data);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadStatus('error');
      showToast?.('Upload failed. Check console for details.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const filteredMedia = filter === 'all' ? mediaItems : mediaItems.filter((m) => m.fileType === filter);

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <header>
        <h2 className="text-2xl font-bold text-slate-800">Files & Media</h2>
        <p className="text-slate-500 mt-1">Access site photos, videos, and project documents.</p>
      </header>

      <div className="grid gap-6 mb-6">
        <Card className="p-6 bg-white border border-slate-200 rounded-3xl shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h3 className="font-bold text-slate-800 text-lg">Upload a file</h3>
              <p className="text-slate-500 text-sm mt-1">Upload updates or documents directly from your portal access.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => document.getElementById('clientMediaFileInput')?.click()}
                className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-3 rounded-xl font-semibold transition-colors"
              >
                Pick file
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading}
                className={`bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-xl font-semibold transition-colors ${uploading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {uploading ? 'Uploading…' : 'Upload File'}
              </button>
            </div>
          </div>

          <form onSubmit={handleUpload} className="mt-6 grid gap-4">
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Project update, invoice, contract..."
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">File type</label>
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm bg-slate-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="photo">Photo</option>
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Selected file</label>
              <div className="rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-600">
                {selectedFile ? selectedFile.name : 'No file selected yet.'}
              </div>
              <input
                id="clientMediaFileInput"
                type="file"
                accept="image/*,video/*,application/pdf"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="mt-2 block w-full text-sm text-slate-700"
              />
            </div>

            {uploadStatus === 'success' && (
              <p className="text-sm text-emerald-700">Upload completed successfully.</p>
            )}
            {uploadStatus === 'error' && (
              <p className="text-sm text-rose-700">Upload failed. Please try again.</p>
            )}
          </form>
        </Card>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {filteredMedia.map((item) => (
          <Card key={item.id} className="overflow-hidden cursor-pointer hover:border-red-300 hover:shadow-md transition-all group" onClick={() => onPreview(item)}>
            <div className="h-36 bg-slate-100 flex items-center justify-center relative overflow-hidden">
              {item.fileType === 'photo' && (
                <img
                  src={secureUrls[item.id] || `https://placehold.co/600x400/e2e8f0/64748b?text=${encodeURIComponent(item.fileName)}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  alt={item.fileName}
                />
              )}
              {item.fileType === 'video' && (
                <div className="bg-slate-800 w-full h-full flex items-center justify-center group-hover:bg-slate-700 transition-colors">
                  <Play size={32} className="text-white/80 group-hover:text-white" />
                </div>
              )}
              {item.fileType === 'document' && (
                <div className="bg-red-50 w-full h-full flex items-center justify-center">
                  <FileText size={40} className="text-red-300 group-hover:text-red-400 transition-colors" />
                </div>
              )}
            </div>
            <div className="p-4 bg-white relative z-10">
              <h4 className="font-bold text-sm text-slate-800 line-clamp-2">{item.fileName}</h4>
              <p className="text-xs text-slate-500 mt-2">{new Date(item.uploadDate || item.date || Date.now()).toLocaleDateString()}</p>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export const ClientChat = ({ projectId }) => {
  const [messages, setMessages] = useState([
    { id: 1, text: 'Hi John, confirming the tile selection for the ensuite.', sender: 'builder', time: '09:15 AM' }
  ]);
  const [input, setInput] = useState('');
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!client || !projectId) return;

    // Subscribe to live updates via observeQuery and filter by projectId
    const sub = client.models.Message.observeQuery({ limit: 200 }).subscribe(({ items }) => {
      const projectMessages = items
        .filter((m) => m.projectId === projectId)
        .map((m) => ({ id: m.id, text: m.text, sender: m.sender, time: new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }));
      setMessages(projectMessages);
    });

    return () => sub.unsubscribe();
  }, [projectId]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const now = new Date();
    const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (client && projectId && client.models?.Message?.create) {
      try {
        await client.models.Message.create({ projectId, text: input, sender: 'client', timestamp: now.toISOString() });
        setInput('');
      } catch (err) {
        console.warn('Failed to send message to AWS:', err);
        // fallback to local UI
        const newMsg = { id: Date.now(), text: input, sender: 'client', time: timeString };
        setMessages((prev) => [...prev, newMsg]);
        setInput('');
      }
    } else {
      const newMsg = { id: Date.now(), text: input, sender: 'client', time: timeString };
      setMessages((prev) => [...prev, newMsg]);
      setInput('');

      setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now() + 1,
            text: 'Thanks for the message. I will check on this and update you shortly.',
            sender: 'builder',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        ]);
      }, 1500);
    }
  };

  return (
    <Card className="flex flex-col h-[600px] max-w-3xl mx-auto animate-[fadeIn_0.3s_ease-out]">
      <div className="p-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <div>
          <h2 className="font-bold text-slate-800">Project Messages</h2>
          <p className="text-xs text-slate-500">Direct line to your builder</p>
        </div>
      </div>
      <div ref={chatRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex gap-3 max-w-[80%] ${msg.sender === 'client' ? 'ml-auto flex-row-reverse' : ''} animate-[fadeIn_0.2s_ease-out]`}>
            <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold text-white shadow-sm ${msg.sender === 'client' ? 'bg-slate-400' : 'bg-red-600'}`}>
              {msg.sender === 'client' ? 'ME' : 'MB'}
            </div>
            <div className={`flex flex-col ${msg.sender === 'client' ? 'items-end' : 'items-start'}`}>
              <div className={`p-3 rounded-2xl shadow-sm text-sm border ${msg.sender === 'client' ? 'bg-red-600 text-white border-red-700 rounded-tr-sm' : 'bg-white text-slate-700 border-slate-200 rounded-tl-sm'}`}>
                {msg.text}
              </div>
              <span className="text-[10px] text-slate-400 mt-1">{msg.time}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 bg-white border-t border-slate-100">
        <form onSubmit={handleSend} className="flex gap-2 items-center">
          <button type="button" className="p-2 text-slate-400 hover:text-red-600 transition-colors">
            <Paperclip size={20} />
          </button>
          <input
            type="text"
            className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-slate-50"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
          <button type="submit" className="bg-red-600 text-white p-3 rounded-xl hover:bg-red-700 shadow-sm transition-colors flex items-center justify-center h-11 w-11">
            <Send size={18} />
          </button>
        </form>
      </div>
    </Card>
  );
};

export const AdminProjects = ({ projects, onImpersonate, showToast }) => {
  const [showNewProject, setShowNewProject] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [xeroProjects, setXeroProjects] = useState([]);

  const handleFetchXeroProjects = async () => {
    setIsSyncing(true);

    try {
      if (xeroApiUrl) {
        const result = await callXeroSyncEndpoint('syncProjects');
        const projects = result.projects?.Projects || result.projects || result.raw?.Projects || result.raw || [];
        setXeroProjects(Array.isArray(projects) ? projects : [projects]);
        showToast(`Synced ${Array.isArray(projects) ? projects.length : 0} Xero project(s).`, 'success');
      } else {
        setXeroProjects(MOCK_XERO_PROJECTS);
        showToast('Mock Xero projects loaded. Configure REACT_APP_XERO_SYNC_URL for live sync.', 'success');
      }
    } catch (err) {
      console.error('Xero project sync failed:', err);
      setXeroProjects(MOCK_XERO_PROJECTS);
      showToast(`Xero sync failed: ${err.message || err}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCreateProject = (e) => {
    e.preventDefault();
    showToast('Project created and linked to Xero successfully.', 'success');
    setShowNewProject(false);
  };

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Builder Projects</h2>
          <p className="text-slate-500 mt-1">Manage active builds and override financial margins securely.</p>
        </div>
        <button
          onClick={() => {
            setShowNewProject(true);
            handleFetchXeroProjects();
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors shadow-sm flex items-center gap-2"
        >
          <Plus size={18} /> New Project (from Xero)
        </button>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {projects.map((p) => (
          <Card key={p.id} className="flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-100 relative">
              {p.status === 'active' && <span className="absolute top-6 right-6 w-3 h-3 rounded-full bg-red-500 animate-pulse" />}
              <h3 className="font-bold text-slate-800 text-lg mb-1">{p.name || p.siteAddress}</h3>
              <p className="text-xs text-slate-500">{p.address || p.siteAddress}</p>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 border border-slate-300">
                  {p.clientName ? p.clientName.split(' ').map((n) => n[0]).join('') : 'CL'}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">{p.clientName || p.clientEmail}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Client</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-slate-50 flex-1 flex flex-col gap-4">
              <div>
                <div className="flex justify-between items-end mb-1">
                  <p className="text-xs font-semibold text-slate-600 uppercase">Progress</p>
                  <span className="text-sm font-bold text-red-600">{p.progress || 0}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-red-500 h-2 rounded-full" style={{ width: `${p.progress || 0}%` }} />
                </div>
              </div>
              <div className="mt-auto bg-white border border-slate-200 rounded-lg p-3 flex justify-between items-center shadow-sm">
                <div>
                  <p className="text-xs font-semibold text-slate-700">Builder Margin</p>
                  <p className="text-[10px] text-slate-500">Overrides global setting</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="w-16 border border-slate-300 rounded px-2 py-1 text-sm font-bold text-right focus:outline-none focus:ring-2 focus:ring-red-500"
                    defaultValue={p.builderMarginPercent || 15}
                    onBlur={(e) => {
                      showToast('Project margin updated in DynamoDB', 'success');
                    }}
                  />
                  <span className="text-sm font-bold text-slate-500">%</span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-white border-t border-slate-100 flex gap-2">
              <button
                onClick={() => onImpersonate(p.id)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <Eye size={16} /> View Portal
              </button>
            </div>
          </Card>
        ))}
      </div>

      {showNewProject && (
        <div className="fixed inset-0 bg-slate-900/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
          <Card className="max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <LinkIcon className="text-blue-500" size={24} />
                Link Xero Project
              </h3>
              <button onClick={() => setShowNewProject(false)} className="text-slate-400 hover:text-slate-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Select Xero Project</label>
                <div className="relative">
                  {isSyncing ? (
                    <div className="w-full border border-slate-300 rounded-lg p-3 bg-slate-50 text-slate-500 flex items-center gap-2">
                      <RefreshCw size={16} className="animate-spin" /> Fetching from Xero...
                    </div>
                  ) : (
                    <select required className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 outline-none appearance-none bg-white">
                      <option value="" disabled>
                        Select an unlinked project...
                      </option>
                      {xeroProjects.map((xp) => (
                        <option key={xp.id} value={xp.id}>
                          {xp.name} ({xp.contactName})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">Only projects marked "In Progress" in Xero are shown.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Portal Project Name</label>
                <input type="text" required className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 outline-none" placeholder="e.g. The Smith Residence" />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Default Builder Margin (%)</label>
                <input type="number" required defaultValue="15" step="0.1" className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 outline-none" />
              </div>

              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowNewProject(false)} className="flex-1 px-4 py-3 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={isSyncing} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-bold py-3 px-4 rounded-xl transition-colors shadow-sm flex justify-center items-center gap-2">
                  <Check size={18} /> Import & Create
                </button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export const AdminClients = ({ showToast }) => {
  const [showInvite, setShowInvite] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [xeroContacts, setXeroContacts] = useState([]);

  const handleFetchXeroContacts = async () => {
    setIsSyncing(true);

    try {
      if (xeroApiUrl) {
        const result = await callXeroSyncEndpoint('contacts');
        const contacts = result.contacts || result.raw?.Contacts || result.raw || [];
        setXeroContacts(Array.isArray(contacts) ? contacts : [contacts]);
        showToast(`Fetched ${Array.isArray(contacts) ? contacts.length : 0} Xero contact(s).`, 'success');
      } else {
        setXeroContacts(MOCK_XERO_CONTACTS);
        showToast('Mock Xero contacts loaded. Configure REACT_APP_XERO_SYNC_URL for live sync.', 'success');
      }
    } catch (err) {
      console.error('Xero contacts sync failed:', err);
      setXeroContacts(MOCK_XERO_CONTACTS);
      showToast(`Xero contact sync failed: ${err.message || err}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSendInvite = (e) => {
    e.preventDefault();
    showToast('Invitation sent via AWS Cognito successfully.', 'success');
    setShowInvite(false);
  };

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Client Management</h2>
          <p className="text-slate-500 mt-1">Manage portal access and link clients to Xero contacts.</p>
        </div>
        <button
          onClick={() => {
            setShowInvite(true);
            handleFetchXeroContacts();
          }}
          className="bg-slate-800 hover:bg-slate-900 text-white font-bold py-2.5 px-5 rounded-xl transition-colors shadow-sm flex items-center gap-2"
        >
          <Users size={18} /> Invite Client
        </button>
      </header>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Client Details</th>
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Assigned Project</th>
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Xero Status</th>
                <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {MOCK_CLIENTS.map((client) => (
                <tr key={client.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold border border-slate-300">
                        {client.name.split(' ').map((n) => n[0]).join('')}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-800">{client.name}</p>
                        <p className="text-xs text-slate-500">{client.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-sm font-semibold text-slate-700 capitalize">{client.projectId} Build</p>
                  </td>
                  <td className="p-4">
                    <Badge variant="info" className="flex items-center gap-1 w-fit">
                      <LinkIcon size={12} /> Synced Contact
                    </Badge>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button className="p-2 text-slate-400 hover:text-slate-800 transition-colors bg-white border border-slate-200 rounded-lg shadow-sm">
                      <Settings size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {showInvite && (
        <div className="fixed inset-0 bg-slate-900/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-[fadeIn_0.2s_ease-out]">
          <Card className="max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="text-slate-800" size={24} />
                Invite Client & Link Xero
              </h3>
              <button onClick={() => setShowInvite(false)} className="text-slate-400 hover:text-slate-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSendInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Link Xero Contact</label>
                <div className="relative">
                  {isSyncing ? (
                    <div className="w-full border border-slate-300 rounded-lg p-3 bg-slate-50 text-slate-500 flex items-center gap-2">
                      <RefreshCw size={16} className="animate-spin" /> Fetching contacts...
                    </div>
                  ) : (
                    <select required className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 outline-none appearance-none bg-white">
                      <option value="" disabled>
                        Select a Xero Contact...
                      </option>
                      {xeroContacts.map((xc) => (
                        <option key={xc.id} value={xc.id}>
                          {xc.name} ({xc.email})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Assign to Portal Project</label>
                <select required className="w-full border border-slate-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 outline-none appearance-none bg-white">
                  <option value="" disabled>
                    Select an existing portal project...
                  </option>
                  <option value="smith">The Smith Residence</option>
                  <option value="miller">The Miller Build</option>
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3 text-sm text-blue-800 mt-2">
                <p>AWS Cognito will automatically email the client with secure login credentials and tie their account to the selected project.</p>
              </div>

              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowInvite(false)} className="flex-1 px-4 py-3 rounded-xl border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={isSyncing} className="flex-1 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-500 text-white font-bold py-3 px-4 rounded-xl transition-colors shadow-sm flex justify-center items-center gap-2">
                  <Send size={18} /> Send Invite
                </button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export const AdminMedia = ({ projects, showToast }) => {
  const [selectedProjectId, setSelectedProjectId] = useState(projects?.[0]?.id || '');
  const [uploadType, setUploadType] = useState('photo');
  const [title, setTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (projects?.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedProjectId || !title) {
      showToast('Please select a project, file, and title before uploading.', 'error');
      return;
    }

    setUploading(true);
    const key = `media/${selectedProjectId}/${Date.now()}-${selectedFile.name}`;
    try {
      const uploadResult = await uploadData(key, selectedFile, {
        level: 'protected',
        contentType: selectedFile.type || 'application/octet-stream'
      });
      const filePath = typeof uploadResult === 'string' ? uploadResult : uploadResult?.key || key;

      if (client?.models?.Media?.create) {
        await client.models.Media.create({
          projectId: selectedProjectId,
          fileName: title,
          fileType: uploadType,
          url: filePath,
          uploadedBy: 'builder',
          uploadDate: new Date().toISOString(),
          description: title,
          visibility: 'protected'
        });
      }

      showToast('File uploaded to AWS S3 securely', 'success');
      setTitle('');
      setSelectedFile(null);
      setUploadType('photo');
      e.target.reset();
    } catch (err) {
      console.error('Upload failed:', err);
      showToast('Upload failed. Check console for details.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const primaryProject = projects?.[0] || { media: [], name: 'Project' };

  return (
    <div className="space-y-8 animate-[fadeIn_0.3s_ease-out]">
      <header>
        <h2 className="text-2xl font-bold text-slate-800">Site Media & Docs</h2>
        <p className="text-slate-500 mt-1">Upload securely to S3. Clients access via pre-signed URLs.</p>
      </header>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <Card className="p-6 xl:col-span-1 h-fit">
          <h3 className="font-bold text-slate-800 mb-4 border-b border-slate-100 pb-4">Upload New File</h3>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Target Project</label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white focus:ring-2 focus:ring-red-500 outline-none"
              >
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || p.siteAddress}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">File Type</label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white focus:ring-2 focus:ring-red-500 outline-none"
              >
                <option value="photo">Photo</option>
                <option value="video">Video</option>
                <option value="document">Document</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-red-500 outline-none"
                placeholder="e.g. Frame complete"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Select File</label>
              <input
                type="file"
                accept="image/*,application/pdf,video/*"
                onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                className="w-full text-sm text-slate-700 border border-slate-300 rounded-lg p-2.5 bg-white focus:ring-2 focus:ring-red-500 outline-none"
              />
              {selectedFile && <p className="text-xs text-slate-500 mt-2">Selected: {selectedFile.name}</p>}
            </div>
            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-red-600 text-white py-2.5 rounded-lg font-bold shadow-sm hover:bg-red-700 transition-colors flex justify-center gap-2 items-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Upload size={18} /> {uploading ? 'Uploading...' : 'Upload to Portal'}
            </button>
          </form>
        </Card>
        <Card className="p-6 xl:col-span-2">
          <h3 className="font-bold text-slate-800 mb-4 border-b border-slate-100 pb-4">Recent Uploads</h3>
          <div className="space-y-3">
            {primaryProject.media.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-4 border border-slate-200 rounded-xl hover:shadow-md transition-shadow group">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${m.type === 'photo' ? 'bg-blue-100 text-blue-600' : m.type === 'video' ? 'bg-purple-100 text-purple-600' : 'bg-red-100 text-red-600'}`}>
                    {m.type === 'photo' ? <ImageIcon size={24} /> : m.type === 'video' ? <Play size={24} /> : <FileText size={24} />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800">{m.title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{m.date} • {primaryProject.name}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};